import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
// Api
import {
  REFUND_TICKET_ORDER,
  REVOKE_COMP_TICKET_ORDER,
} from 'api/ticketing/mutations';
// Types
import {
  RefundTicketOrder,
  RefundTicketOrderVariables,
} from 'api/ticketing/types/RefundTicketOrder';
import {
  RevokeCompTicketOrder,
  RevokeCompTicketOrderVariables,
} from 'api/ticketing/types/RevokeCompTicketOrder';
// Helpers
import { runTicketingChange } from 'pages/Events/runTicketingChange';

type ActionableOrder = { orderId: string; reference: string };

export type OrderActions = {
  /** Asks Stripe for a full refund; resolves to whether the request went through. */
  refundOrder: (order: ActionableOrder) => Promise<boolean>;
  revokeComp: (order: ActionableOrder) => Promise<boolean>;
};

export const useOrderActions = (): OrderActions => {
  const [refundMutation] = useMutation<
    RefundTicketOrder,
    RefundTicketOrderVariables
  >(REFUND_TICKET_ORDER);
  const [revokeMutation] = useMutation<
    RevokeCompTicketOrder,
    RevokeCompTicketOrderVariables
  >(REVOKE_COMP_TICKET_ORDER);

  const refundOrder = useCallback(
    ({ orderId, reference }: ActionableOrder): Promise<boolean> =>
      runTicketingChange(
        () =>
          refundMutation({
            variables: { input: { orderId } },
          }),
        `Refund for ${reference} requested. It shows as refunded once Stripe confirms.`,
        'Failed to refund the order'
      ),
    [refundMutation]
  );

  const revokeComp = useCallback(
    ({ orderId, reference }: ActionableOrder): Promise<boolean> =>
      runTicketingChange(
        () =>
          revokeMutation({
            variables: { input: { orderId } },
          }),
        `Comp ${reference} revoked`,
        'Failed to revoke the comp'
      ),
    [revokeMutation]
  );

  return { refundOrder, revokeComp };
};
